import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SupplierDataCheckAction } from "../../../../../libs/coreapi-dto/dirs/supplierDataCheck";
import { IInvoiceItemGetDTO } from "../../../../../libs/coreapi-dto/documents/invoiceItem";

interface IErrors {
    itemName: string;
    producerPrice: number;
    registerPrice: number;
    message: string;
}

interface IValidationCallbacks {
    onCancel: () => void;
    onConfirm?: () => void;
}

interface IModalConfig {
    visible: boolean;
    header: string;
    warning: string;
    items: string;
    question: string;
    action: SupplierDataCheckAction;
    onConfirm?: () => void;
    onCancel: () => void;
    largeTextArea?: boolean;
    textAreaInputStyle?: React.CSSProperties;
}

export const useProducerPriceValidation = () => {
    const [modalConfig, setModalConfig] = useState<IModalConfig | null>(null);
    
    const closeModal = () => {
        setModalConfig(null);
    };
    const { t } = useTranslation();

    const validateProducerPriceVsRegisterPrice = (
        items: IInvoiceItemGetDTO[],
        action: SupplierDataCheckAction,
        callbacks?: IValidationCallbacks
    ) => {
        const errors: IErrors[] = [];

        if (!items || !Array.isArray(items)) {
            return errors;
        }

        items.forEach((item) => {
            const producerPrice = item.producerPrice || 0;
            const registerPrice = item.registerPrice || 0;
            const isGnvls = item?.isGnvls ?? false;

            if (registerPrice > 0 && producerPrice > registerPrice && isGnvls) {
                const itemName = item.goods?.displayName;
                errors.push({
                    itemName: itemName,
                    producerPrice: producerPrice,
                    registerPrice: registerPrice,
                    message: `"${itemName}" \n ${t('documents.invoice.messages.producerPriceFullText')} \n`
                });
            }
        });

        if (errors.length === 0 && action !== "NONE") {
            callbacks?.onConfirm?.();
            return errors;
        }

        if (errors.length > 0) {
            const errorMessages = errors.map(error => error.message).join('\n');
            
            if (action === "WARN") {
                setModalConfig({
                    visible: true,
                    header: t('documents.invoice.messages.producerPriceTitle'),
                    warning: '',
                    items: errorMessages,
                    question: t('documents.invoice.messages.continue'),
                    action: "WARN",
                    onCancel: () => {
                        closeModal();
                        callbacks?.onCancel();
                    },
                    onConfirm: () => {
                        closeModal();
                        callbacks?.onConfirm?.();
                    },
                    largeTextArea: true,
                    textAreaInputStyle: { backgroundColor: "rgb(255, 204, 51, 0.2)", color: "black" }
                });
            } else if (action === "LOCK") {
                setModalConfig({
                    visible: true,
                    header: t('documents.invoice.messages.producerPriceTitle'),
                    warning: '',
                    items: errorMessages,
                    question: '',
                    action: "LOCK",
                    onCancel: () => {
                        closeModal();
                        callbacks?.onCancel();
                    },
                    largeTextArea: true,
                    textAreaInputStyle: { backgroundColor: "rgb(204, 51, 51, 0.2)", color: "black" }
                });
            }
        }

        return errors;
    };
   
    return {
        validateProducerPriceVsRegisterPrice,
        modalConfig,
        closeModal
    };
};
